import { UploadOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Col,
  Collapse,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useMemo, useState } from 'react';
import {
  useGroupMembers,
  useModelGroups,
  useModelTest,
  useStartGroupTest,
  useUploadMedia,
} from '../../api/hooks';
import { extractErrorMessage } from '../../api/client';
import { UPLOAD_TYPES, type ParamField } from './modelParams';
import { MediaResult } from '../MediaResult';

/** Nhan dien URL video de xem truoc dung the <video> thay vi <img>. */
const VIDEO_RE = /\.(mp4|webm|mov|m4v|avi|mkv)(\?|#|$)/i;

function compact(obj: Record<string, unknown>): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    o[k] = v;
  }
  return o;
}

/**
 * Kiểm thử NHÓM MODEL — giống trang kiểm thử provider: form trái, kết quả + logs phải.
 * Ảnh/video: vừa UPLOAD (tải lên qua 1 nguồn trong nhóm) vừa dán URL công khai.
 * Gửi tham số chung (canonical) → nhóm tự chọn model + xoay vòng.
 */
export function GroupTestTab({ groupId }: { groupId: string }) {
  const { message } = AntApp.useApp();
  const groups = useModelGroups();
  const membersQ = useGroupMembers(groupId);
  const group = groups.data?.find((g) => g.id === groupId);

  const fields = useMemo(
    () => ((group?.paramsSchema as ParamField[]) || []).filter((f) => f.enabled !== false),
    [group?.paramsSchema],
  );
  // Nguồn để upload file cục bộ (lấy member đầu có model) — trả URL công khai dùng cho mọi member.
  const uploadProviderId = useMemo(
    () => (membersQ.data || []).find((m) => m.model?.provider?.id)?.model?.provider?.id,
    [membersQ.data],
  );
  // Model của member đó: config model ghi đè config provider khi upload (vd Dola video
  // dùng /api/v1/videos/upload-reference và cần `key` thay vì preview_url).
  const uploadModelId = useMemo(
    () => (membersQ.data || []).find((m) => m.model?.provider?.id)?.model?.id,
    [membersQ.data],
  );
  const upload = useUploadMedia(uploadProviderId || '');

  const start = useStartGroupTest(groupId);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [advanced, setAdvanced] = useState('');
  const [urlInputs, setUrlInputs] = useState<Record<string, string>>({});
  const [testId, setTestId] = useState<string>();
  const result = useModelTest(testId);

  const setValue = (name: string, v: unknown) => setValues((p) => ({ ...p, [name]: v }));
  const isVisible = (f: ParamField) => !f.showWhen || values[f.showWhen.field] === f.showWhen.equals;
  const isArrayField = (t: string) => t === 'image[]' || t === 'video[]';

  const appendUrl = (f: ParamField, url: string) => {
    if (!url) return;
    if (isArrayField(f.type)) {
      const cur = (values[f.name] as string[]) || [];
      setValue(f.name, [...cur, url]);
    } else {
      setValue(f.name, url);
    }
  };

  const doUpload = async (f: ParamField, file: File) => {
    if (!uploadProviderId) {
      message.error('Nhóm chưa có model nào để tải ảnh — hãy dán URL công khai.');
      return;
    }
    const mediaType = f.type.startsWith('video') ? 'video' : 'image';
    const fd = new FormData();
    fd.append('mediaType', mediaType);
    if (uploadModelId) fd.append('modelId', uploadModelId);
    fd.append('file', file);
    try {
      const r = await upload.mutateAsync(fd);
      if (r?.url) appendUrl(f, r.url);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const addUrlManually = (f: ParamField) => {
    const u = (urlInputs[f.name] || '').trim();
    if (!u) return;
    appendUrl(f, u);
    setUrlInputs((p) => ({ ...p, [f.name]: '' }));
  };

  const removeUrl = (f: ParamField, url: string) => {
    if (isArrayField(f.type)) {
      setValue(f.name, ((values[f.name] as string[]) || []).filter((x) => x !== url));
    } else {
      setValue(f.name, undefined);
    }
  };

  const onRun = async () => {
    const resolved: Record<string, unknown> = {};
    for (const f of fields) {
      if (!isVisible(f)) continue;
      let v = values[f.name];
      if (f.type === 'json' && typeof v === 'string' && v.trim()) {
        try {
          v = JSON.parse(v);
        } catch {
          message.error(`Tham số "${f.name}" không phải JSON hợp lệ`);
          return;
        }
      }
      resolved[f.name] = v;
    }
    let extra: Record<string, unknown> = {};
    if (advanced.trim()) {
      try {
        extra = JSON.parse(advanced);
      } catch {
        message.error('Tham số nâng cao (JSON) không hợp lệ');
        return;
      }
    }
    try {
      const { testId: id } = await start.mutateAsync({ input: { ...compact(resolved), ...extra } });
      setTestId(id);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const renderUpload = (f: ParamField) => {
    const accept = f.type === 'media' ? 'image/*,video/*,audio/*' : f.type.startsWith('video') ? 'video/*' : f.type === 'audio' ? 'audio/*' : 'image/*';
    const cur = isArrayField(f.type)
      ? ((values[f.name] as string[]) || [])
      : values[f.name]
        ? [values[f.name] as string]
        : [];
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <div
          tabIndex={0}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files ?? []);
            if (files.length) {
              e.preventDefault();
              files.forEach((file) => doUpload(f, file));
            }
          }}
          style={{ outline: 'none' }}
        >
          <Upload.Dragger
            accept={accept}
            multiple={isArrayField(f.type)}
            showUploadList={false}
            beforeUpload={(file) => {
              doUpload(f, file);
              return Upload.LIST_IGNORE;
            }}
            style={{ padding: '8px 12px' }}
          >
            <p style={{ margin: 0, fontSize: 13 }}>
              <UploadOutlined /> Kéo-thả / bấm để chọn{f.max ? ` · tối đa ${f.max}` : ''}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#8c8c8c' }}>Hoặc dán ảnh (Ctrl/Cmd+V), hoặc dán URL bên dưới.</p>
          </Upload.Dragger>
        </div>
        <label style={{ fontSize: 12, color: '#595959', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Nút gốc trình duyệt:
          <input
            type="file"
            accept={accept}
            multiple={isArrayField(f.type)}
            onChange={(e) => {
              Array.from(e.target.files ?? []).forEach((file) => doUpload(f, file));
              e.target.value = '';
            }}
          />
        </label>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="hoặc dán URL công khai (https://…)"
            value={urlInputs[f.name] || ''}
            onChange={(e) => setUrlInputs((p) => ({ ...p, [f.name]: e.target.value }))}
            onPressEnter={() => addUrlManually(f)}
          />
          <Button onClick={() => addUrlManually(f)}>Thêm URL</Button>
        </Space.Compact>
        {upload.isPending && <Spin size="small" />}
        {cur.length > 0 && (
          <Space wrap>
            {cur.map((u) => (
              <div key={u} style={{ position: 'relative' }}>
                {VIDEO_RE.test(u) ? (
                  <video src={u} style={{ width: 80, height: 80, objectFit: 'cover' }} />
                ) : (
                  <img src={u} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                )}
                <Button size="small" danger style={{ position: 'absolute', top: -8, right: -8 }} onClick={() => removeUrl(f, u)}>
                  ×
                </Button>
              </div>
            ))}
          </Space>
        )}
      </Space>
    );
  };

  const renderField = (f: ParamField) => {
    if (UPLOAD_TYPES.includes(f.type)) return renderUpload(f);
    const v = values[f.name];
    switch (f.type) {
      case 'textarea':
        return <Input.TextArea rows={3} value={v as string} onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} value={v as number} onChange={(x) => setValue(f.name, x)} />;
      case 'select':
        return (
          <Select
            style={{ width: '100%' }}
            allowClear
            value={v as string}
            onChange={(x) => setValue(f.name, x)}
            options={(f.options || []).map((o) => ({ value: o, label: String(o) }))}
          />
        );
      case 'bool':
        return <Switch checked={!!v} onChange={(x) => setValue(f.name, x)} />;
      case 'json':
        return (
          <Input.TextArea rows={2} style={{ fontFamily: 'monospace' }} placeholder="[] hoặc { }" value={v as string} onChange={(e) => setValue(f.name, e.target.value)} />
        );
      default:
        return <Input value={v as string} onChange={(e) => setValue(f.name, e.target.value)} />;
    }
  };

  const data = result.data;

  return (
    <Row gutter={16}>
      {/* TRÁI: form tham số */}
      <Col xs={24} lg={13}>
        <Typography.Paragraph type="secondary">
          Kiểm thử nhóm chạy như vận hành thật: gửi tham số chung (canonical) → nhóm chọn model
          mặc định/ưu tiên và <b>tự xoay vòng</b> khi lỗi.
        </Typography.Paragraph>
        {fields.length === 0 && (
          <Typography.Text type="warning">
            Nhóm chưa có tham số chung — khai ở "Tham số chung của nhóm" rồi "Lưu tham số nhóm".
          </Typography.Text>
        )}
        {fields.filter(isVisible).map((f) => (
          <div key={f.name} style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <b>{f.label || f.name}</b> {f.required && <span style={{ color: 'red' }}>*</span>} <Tag>{f.name}</Tag>
            </div>
            {renderField(f)}
          </div>
        ))}
        <Collapse
          size="small"
          style={{ margin: '8px 0 12px' }}
          items={[
            {
              key: 'adv',
              label: 'Tham số nâng cao (JSON) — gộp thêm vào input',
              children: (
                <Input.TextArea rows={3} style={{ fontFamily: 'monospace' }} placeholder='{ "seed": 123 }' value={advanced} onChange={(e) => setAdvanced(e.target.value)} />
              ),
            },
          ]}
        />
        <Button type="primary" loading={start.isPending} onClick={onRun}>
          Chạy kiểm thử nhóm
        </Button>
      </Col>

      {/* PHẢI: kết quả + logs */}
      <Col xs={24} lg={11}>
        <Typography.Title level={5}>Kết quả kiểm thử</Typography.Title>
        {!testId && <Typography.Text type="secondary">Chạy kiểm thử để xem kết quả.</Typography.Text>}
        {testId && (
          <div>
            <Space wrap>
              <b>Trạng thái:</b>
              {data?.status === 'succeeded' ? (
                <Tag color="success">Thành công</Tag>
              ) : data?.status === 'failed' ? (
                <Tag color="error">Thất bại</Tag>
              ) : (
                <Tag color="processing">Đang chạy…</Tag>
              )}
              {data?.runner && <Tag>{data.runner === 'gateway' ? 'Gateway' : 'Worker'}</Tag>}
            </Space>
            {data?.note && (
              <Typography.Paragraph type="secondary" style={{ marginTop: 6 }}>
                {data.note}
              </Typography.Paragraph>
            )}
            {data?.error && (
              <Typography.Paragraph type="danger" style={{ marginTop: 8 }}>
                Lỗi ({data.error.class}): {data.error.message}
              </Typography.Paragraph>
            )}
            {data?.status === 'succeeded' && <div style={{ marginTop: 12 }}><MediaResult output={data.output} /></div>}
            {!!data?.logs?.length && (
              <Collapse
                size="small"
                style={{ marginTop: 12 }}
                items={[
                  {
                    key: 'logs',
                    label: `Nhật ký (${data.logs.length})`,
                    children: (
                      <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: 12 }}>
                        {data.logs.map((l, i) => (
                          <div key={i} style={{ marginBottom: 6 }}>
                            <Tag>{l.type}</Tag> {l.message}
                            {l.data ? (
                              <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', fontSize: 11 }}>
                                {JSON.stringify(l.data, null, 2)}
                              </pre>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>
        )}
      </Col>
    </Row>
  );
}
